import React, { useCallback, useContext, useEffect, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { OnPasswordRequiredApi } from "iam-client/dist/wrapper/logInFlow";
import { useMessageSource } from "react-message-source";
import Email from "../../../pages/shared/components/email";
import PasswordField from "../../../pages/shared/components/formik/text-input/password";
import ApplicationPageContent from "../../../ui/layout/page/content/application";
import ContentFooter from "../../../ui/layout/page/content/footer";
import NavigationBar from "../../../ui/layout/page/content/footer/navigation-bar";
import FormContainer from "../../../ui/layout/page/forms-container/form-base-container";
import InputContainer from "../../../ui/layout/page/forms-container/form-item-container/input-container";
import TypographyContainer from "../../../ui/layout/page/typography-container/typography-item-container";
import TypographyItemContainer from "../../../ui/layout/page/typography-container/typography-item-container";
import CustomLink from "../../../ui/widgets/link";
import CustomTypography from "../../../ui/widgets/typography";
import { displayErrorInField } from "../../../util/formik";
import useLoginValidationSchema from "../validator";
import { LanguageContext } from "../../../context/languageContext";

import logging from "../../../util/logging";
import { useNavigate } from "react-router-dom";
const log = logging("log-in page");

interface LoginModel {
  emailBusiness: string;
  password: string;
}

const Login = ({
  onNext,
}: {
  onNext: OnPasswordRequiredApi;
}): React.JSX.Element => {
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();

  const { getMessage } = useMessageSource();

  const [formikActions, setFormikActions] =
    useState<FormikHelpers<LoginModel>>();

  const formErrors = onNext && onNext.errors;

  const clearPasswordField = useCallback(() => {
    formikActions.setFieldValue("password", "", false);
  }, [formikActions]);

  const showError = useCallback(() => {
    displayErrorInField(formikActions, "emailBusiness", " ");
    displayErrorInField(formikActions, "password", getMessage("Login.error"));
  }, [formikActions, getMessage]);

  const handleFailedLogin = useCallback(() => {
    clearPasswordField();
    showError();
  }, [clearPasswordField, showError]);

  useEffect(() => {
    if (formErrors) {
      handleFailedLogin();
    }
  }, [formErrors, handleFailedLogin]);

  const initialValues: LoginModel = {
    emailBusiness: "",
    password: "",
  };

  const doLogin = (values: LoginModel) => {
    if (onNext) {
      onNext.proceed({
        username: values.emailBusiness,
        password: values.password,
      });
    } else {
      log.error(
        "Login flow: Cannot proceed in doLogin, due to a missing callback",
      );
    }
  };
  return (
    <ApplicationPageContent>
      <Formik
        key={language}
        initialValues={initialValues}
        validationSchema={useLoginValidationSchema()}
        onSubmit={(values, actions) => {
          doLogin(values);
          setFormikActions(actions);
        }}
      >
        {({ values, handleSubmit }) => {
          return (
            <Form>
              <FormContainer>
                <TypographyContainer>
                  <TypographyItemContainer>
                    <CustomTypography variant="h3">
                      {getMessage("Login.title")}
                    </CustomTypography>
                    <CustomTypography variant="body1">
                      {getMessage("Login.title-description")}
                    </CustomTypography>
                  </TypographyItemContainer>
                </TypographyContainer>
                <InputContainer>
                  <Email
                    forceReRender
                    name="emailBusiness"
                    value={values.emailBusiness}
                    isPrivate={false}
                  />
                </InputContainer>
                <InputContainer>
                  <PasswordField
                    forceReRender
                    name="password"
                    label={getMessage("Account.password")}
                    type="password"
                  />
                </InputContainer>
                <TypographyContainer>
                  <CustomLink
                    onClick={() => {
                      navigate("/password-reset");
                    }}
                  >
                    {getMessage("Login.link-text")}
                  </CustomLink>
                </TypographyContainer>
              </FormContainer>
              <ContentFooter>
                <NavigationBar
                  onNext={handleSubmit}
                  nextButtonText={getMessage("Login.button-text")}
                />
              </ContentFooter>
            </Form>
          );
        }}
      </Formik>
    </ApplicationPageContent>
  );
};

export default Login;
